import { QueryClientProvider } from '@tanstack/react-query';
import type { EventSubscription } from 'expo-modules-core';
import * as Network from 'expo-network';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { apiService } from '@/services/api-service';
import { dataService } from '@/services/data-service';
import { offlineSyncService } from '@/services/offline-sync-service';
import type { User } from '@/types/models';
import { queryClient } from '@/utils/query-client';

interface DataContextType {
	isOnline: boolean;
	currentUser: User | null;
	refetchAll: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

// Custom hook for accessing data context
export const useData = (): DataContextType => {
	const ctx = useContext(DataContext);
	if (!ctx) {
		throw new Error('useData must be used within a DataProvider');
	}
	return ctx;
};

// Provider component
export const DataProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
	const [isOnline, setIsOnline] = useState(true);
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const previousOnlineRef = useRef(true);

	const updateOnlineStatus = (online: boolean) => {
		setIsOnline(online);
		if (online && !previousOnlineRef.current) {
			queryClient.refetchQueries();
			// Process any queued offline operations
			offlineSyncService.processQueue({
				uploadFile: apiService.uploadFile.bind(apiService),
				createAttachment: (data) => dataService.createAttachment(data as any),
				fetchApi: apiService.fetchApi.bind(apiService),
			}).then((result) => {
				if (result.succeeded > 0) {
					console.log(`OfflineSync: ${result.succeeded} item(s) synced on reconnect`);
					queryClient.refetchQueries();
				}
				if (result.failed > 0) {
					console.warn(`OfflineSync: ${result.failed} item(s) failed to sync`);
				}
			}).catch((error) => {
				console.warn('OfflineSync: Queue processing failed on reconnect', error);
			});
		}
		previousOnlineRef.current = online;
	};

	const computeOnlineState = (state?: Network.NetworkState | null) => {
		if (!state) return true;
		if (typeof state.isInternetReachable === 'boolean') {
			return state.isInternetReachable;
		}
		if (typeof state.isConnected === 'boolean') {
			return state.isConnected;
		}
		return true;
	};

	// Initialize offline sync queue on mount
	useEffect(() => {
		offlineSyncService.initialize();
	}, []);

	// Monitor online/offline status
	useEffect(() => {
		if (Platform.OS === 'web') {
			const handleOnline = () => {
				updateOnlineStatus(true);
			};

			const handleOffline = () => {
				updateOnlineStatus(false);
			};

			// Listen to online/offline events (web only)
			window.addEventListener('online', handleOnline);
			window.addEventListener('offline', handleOffline);

			// Check initial online status
			updateOnlineStatus(navigator.onLine);

			return () => {
				window.removeEventListener('online', handleOnline);
				window.removeEventListener('offline', handleOffline);
			};
		} else {
			let isMounted = true;

			const syncState = (state?: Network.NetworkState | null) => {
				if (!isMounted) return;
				updateOnlineStatus(computeOnlineState(state));
			};

			const fetchInitialState = async () => {
				try {
					const state = await Network.getNetworkStateAsync();
					syncState(state);
				} catch (error) {
					console.warn('Failed to fetch network state', error);
					syncState({ isConnected: true, isInternetReachable: true } as Network.NetworkState);
				}
			};

			const startListener = (): EventSubscription | null => {
				try {
					return Network.addNetworkStateListener((state) => {
						syncState(state);
					});
				} catch (error) {
					console.warn('Failed to subscribe to network changes', error);
					return null;
				}
			};

			fetchInitialState();
			const subscription = startListener();

			return () => {
				isMounted = false;
				subscription?.remove();
			};
		}
	}, []);

	// Monitor current user changes
	useEffect(() => {
		const unsubscribe = apiService.onUserChange((user) => {
			setCurrentUser(user);
		});

		// Get initial user
		apiService.getCurrentUser().then(setCurrentUser);

		return unsubscribe;
	}, []);

	// Function to refetch all queries
	const refetchAll = () => {
		queryClient.refetchQueries();
	};

	// Expose query client to window for debugging (web only)
	useEffect(() => {
		if (process.env.NODE_ENV === 'development' && Platform.OS === 'web') {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).queryClient = queryClient;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).apiService = apiService;

			return () => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				delete (window as any).queryClient;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				delete (window as any).apiService;
			};
		}
	}, []);

	return (
		<QueryClientProvider client={queryClient}>
			<DataContext.Provider value={{
				isOnline,
				currentUser,
				refetchAll,
			}}>
				{children}
			</DataContext.Provider>
		</QueryClientProvider>
	);
};

// Legacy hook for backwards compatibility during migration
export const useDatabase = () => {
	console.warn('useDatabase is deprecated, use React Query hooks from hooks/use-data.ts instead');
	const { refetchAll, currentUser } = useData();
	
	return {
		database: null, // Legacy compatibility
		syncDatabase: refetchAll, // Map to refetch all
		getUserById: async (id: string) => {
			// For backwards compatibility, return current user if IDs match
			return currentUser?.id === id ? currentUser : undefined;
		},
	};
};
